import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Badge } from "@/shared/components/ui/badge";
import type { ManualFlight } from "../../types/manual-flight.types";
import { useFlightSelection } from "../../hooks/useFlightSelection";
import { FlightSelectionBar } from "../flight-selection-bar/FlightSelectionBar";
import { Button } from "@/shared/components/ui/button";
import { formatDuration } from "@/features/orders/components/OrderDetail/OrderDetail";

interface ManualFlightsTableProps {
  previewFlights: ManualFlight[];
  totalPages: number;
  currentPage: number;
  onDelete?: (ids: string[]) => void;
  onHide?: (ids: string[]) => void;
  onPublish?: (ids: string[]) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

type LocalizedText = { ru?: string; en?: string; kz?: string };

const STATUS_MAP: Record<
  string,
  { label: string; variant: "danger" | "muted" | "success" }
> = {
  cancelled: { label: "Отменен", variant: "danger" },
  draft: { label: "Черновик", variant: "muted" },
  active: { label: "Активный", variant: "success" },
};

export function ManualFlightsTable({
  previewFlights,
  totalPages,
  currentPage,
  onDelete,
  onHide,
  onPublish,
  page,
  pageSize,
  onPageChange,
}: ManualFlightsTableProps) {
  const {
    selectedIds,
    toggleOne,
    toggleAll,
    clearSelection,
    allSelected,
    someSelected,
  } = useFlightSelection(previewFlights);
  console.log("selectedIds", selectedIds);

  const selectedArray = Array.from(selectedIds);
  console.log("selectedArray", selectedArray);
  const getRuLocale = (value: unknown): string => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return value;
    try {
      const obj = JSON.parse(trimmed) as LocalizedText;
      return obj.ru ?? obj.en ?? obj.kz ?? "";
    } catch {
      return value;
    }
  };

  const formatPassengersSummary = (
    flight: Pick<ManualFlight, "additionalInfo">
  ): string => {
    const adults = flight.additionalInfo?.adultCount ?? 0;
    const children = flight.additionalInfo?.childCount ?? 0;
    return `${adults} взр. ${children} дет.`;
  };

  return (
    <div className="bg-white rounded-b-lg border">
      {/* Панель действий — появляется только при выборе */}
      <FlightSelectionBar
        selectedCount={selectedIds.size}
        onDelete={() => {
          onDelete?.(selectedArray);
          clearSelection();
        }}
        onHide={() => {
          onHide?.(selectedArray);
          clearSelection();
        }}
        onPublish={() => {
          onPublish?.(selectedArray);
          clearSelection();
        }}
        onClear={clearSelection}
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={toggleAll}
                className="cursor-pointer"
              />
            </TableHead>
            <TableHead>Код рейса</TableHead>
            <TableHead>Дата</TableHead>
            <TableHead>Маршрут</TableHead>
            <TableHead>Авиакомпания</TableHead>
            <TableHead>Оператор</TableHead>
            <TableHead>Время полета</TableHead>
            <TableHead>Сумма</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {previewFlights.map((flight) => (
            <TableRow
              key={flight.id}
              className={selectedIds.has(String(flight.id)) ? "bg-blue-50" : ""}
            >
              <TableCell>
                <input
                  type="checkbox"
                  checked={selectedIds.has(String(flight.id))}
                  onChange={() => toggleOne(String(flight.id))}
                  className="cursor-pointer"
                />
              </TableCell>
              <TableCell className="font-medium">
                {flight.flightNumber}
              </TableCell>
              <TableCell>
                {format(new Date(flight.departureAt), "dd.MM.yyyy", {
                  locale: ru,
                })}
              </TableCell>
              <TableCell>
                {getRuLocale(flight.from.city)} → {getRuLocale(flight.to.city)}
              </TableCell>
              <TableCell>{flight.airline}</TableCell>
              <TableCell>{flight.additionalInfo.operator}</TableCell>
              <TableCell>
                {flight.additionalInfo.FlightDurationMinutes
                  ? formatDuration(flight.additionalInfo.FlightDurationMinutes)
                  : "Не указано"}
              </TableCell>
              <TableCell>
                {flight.originalPrice.toLocaleString("ru-RU")} ₸
              </TableCell>
              <TableCell>
                <Badge
                  className={flight.status}
                  variant={STATUS_MAP[flight.status]?.variant ?? "muted"}
                >
                  {STATUS_MAP[flight.status]?.label ?? flight.status}
                </Badge>
              </TableCell>
              <TableCell>
                <button className="p-1 hover:bg-gray-100 rounded">
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex justify-end items-center gap-2 mt-4">
        <span className="text-sm text-muted-foreground">
          {page} / {totalPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
